import { format, isValid, parseISO } from 'date-fns';
import {
  NativeStackHeaderProps,
  Stack,
  useLocalSearchParams,
} from 'expo-router';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { ListItemLineFormat } from 'shared/components/vxl/ListItem/ListItemTextStack';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import ExpressiveLgText from 'shared/components/vxl/typography/expressive/ExpressiveLgText';
import SectionedContentScreen, {
  SectionedContentScreenModel,
} from 'shared/containers/vxl/SectionedContentScreen/SectionedContentScreen';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { VXLComponent, VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';

const VlocTransactionDetails = () => {
  const {
    amount,
    accountFrom,
    accountTo,
    date,
    description,
    status,
    subcategory,
  } = useLocalSearchParams<{
    amount?: string;
    accountFrom?: string;
    accountTo?: string;
    date?: string;
    description?: string;
    status?: string;
    subcategory: string;
  }>();

  const { t: translatedText } = useTranslation('vloc', {
    keyPrefix: 'transactionDetails',
  });

  const { bottom } = useSafeAreaInsets();

  useViewDidFocusScreenAnalyticsEvent({
    key: '/varo-line-of-credit/transaction-details-custom',
    customAttributes: {
      subcategory,
    },
  });

  const sectionsData: SectionedContentScreenModel = { sections: [] };
  const transactionDetailItems: VXLComponent[] = [];
  const accountDetailItems: VXLComponent[] = [];

  if (description) {
    transactionDetailItems.push({
      id: 'description',
      name: VXLComponentId.LIST_ITEM,
      props: {
        primaryCopy: description,
        secondaryCopy: translatedText('description'),
        showsChevron: false,
        lineFormat: ListItemLineFormat.LABEL,
      },
    });
  }

  if (date) {
    let parsedDate = parseISO(date);
    if (!isValid(parsedDate)) {
      parsedDate = new Date(date);
    }
    const formattedDate = format(parsedDate, 'MMM dd, yyyy');

    transactionDetailItems.push({
      id: 'requestedOn',
      name: VXLComponentId.LIST_ITEM,
      props: {
        primaryCopy: formattedDate,
        secondaryCopy: translatedText('requestedOn'),
        showsChevron: false,
        lineFormat: ListItemLineFormat.LABEL,
      },
    });
  }

  if (status) {
    transactionDetailItems.push({
      id: 'status',
      name: VXLComponentId.LIST_ITEM,
      props: {
        primaryCopy: status,
        secondaryCopy: translatedText('status'),
        showsChevron: false,
        lineFormat: ListItemLineFormat.LABEL,
      },
    });
  }

  sectionsData.sections.push({
    sectionHeader: undefined,
    data: transactionDetailItems,
  });

  if (accountFrom) {
    accountDetailItems.push({
      id: 'fromAccount',
      name: VXLComponentId.LIST_ITEM,
      props: {
        primaryCopy: accountFrom,
        secondaryCopy: translatedText('fromAccount'),
        showsChevron: false,
        lineFormat: ListItemLineFormat.LABEL,
      },
    });
  }

  if (accountTo) {
    accountDetailItems.push({
      id: 'toAccount',
      name: VXLComponentId.LIST_ITEM,
      props: {
        primaryCopy: accountTo,
        secondaryCopy: translatedText('toAccount'),
        showsChevron: false,
        lineFormat: ListItemLineFormat.LABEL,
      },
    });
  }

  if (accountDetailItems.length > 0) {
    sectionsData.sections.push({
      sectionHeader: undefined,
      data: accountDetailItems,
    });
  }

  return (
    <View style={[styles.container, { paddingBottom: bottom }]}>
      <Stack.Screen
        options={{
          title: translatedText('header'),
          headerBackVisible: true,
          headerShown: true,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
        }}
      />

      <ExpressiveLgText style={styles.amountText}>{amount}</ExpressiveLgText>

      <SectionedContentScreen model={sectionsData} />
    </View>
  );
};

const styles = StyleSheet.create({
  amountText: { alignSelf: 'center', marginVertical: theme.spacing.m },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
    paddingVertical: theme.spacing.m,
  },
});

export default VlocTransactionDetails;
